/**
 * Création d'une animation : monologue ou dialogue à 2 / 3 (règles pures :
 * `@/lib/animations/voices`). Chaque réplique sera enregistrée par qui tient
 * le rôle (Speaker 1, 2, 3) dans « Audios animations ».
 */

import { useId } from 'react';
import { cn } from '@jeraisonne/ui';
import {
  duplicateRoleNames,
  NOTES_MAX,
  ROLE_MAX,
  VOICE_SCENARIOS,
  withMode,
  withScenario,
  type VoicesChoice,
} from '@/lib/animations/voices';

const MODES = [
  { key: 'monologue', label: 'Monologue', mode: 'monologue', count: 2 },
  { key: 'dialogue-2', label: 'Dialogue à 2', mode: 'dialogue', count: 2 },
  { key: 'dialogue-3', label: 'Dialogue à 3', mode: 'dialogue', count: 3 },
] as const;

export function AnimationVoicesPicker({
  value,
  onChange,
  disabled = false,
}: {
  value: VoicesChoice;
  onChange: (v: VoicesChoice) => void;
  disabled?: boolean;
}) {
  const id = useId();
  const current = value.mode === 'monologue' ? 'monologue' : `dialogue-${value.count}`;
  const dialogue = value.mode === 'dialogue';
  const setRole = (i: number, name: string) => {
    const roles = [...value.roles];
    roles[i] = name.slice(0, ROLE_MAX);
    onChange({ ...value, roles });
  };

  return (
    <fieldset className="space-y-3" disabled={disabled}>
      <legend className="text-ink-600 mb-1 text-xs font-semibold">Qui parle ?</legend>
      <div role="radiogroup" aria-label="Qui parle" className="flex flex-wrap gap-2">
        {MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            role="radio"
            aria-checked={current === m.key}
            onClick={() => onChange(withMode(value, m.mode, m.count))}
            className={cn(
              'min-h-[2.75rem] rounded-full border px-4 text-sm font-semibold active:scale-[0.98]',
              current === m.key
                ? 'border-primary-500 bg-primary-500 text-white'
                : 'border-ink-200 text-ink-700 bg-white',
            )}
          >
            {m.label}
          </button>
        ))}
      </div>

      {dialogue ? (
        <>
          <div className="space-y-1">
            <p className="text-ink-600 text-xs font-semibold">Scénario</p>
            <div className="flex flex-wrap gap-2">
              {VOICE_SCENARIOS.map((sc) => (
                <button
                  key={sc.id}
                  type="button"
                  aria-pressed={value.scenario === sc.id}
                  onClick={() => onChange(withScenario(value, sc.id))}
                  className={cn(
                    'min-h-[2.75rem] rounded-full border px-3 text-sm active:scale-[0.98]',
                    value.scenario === sc.id
                      ? 'border-primary-500 bg-primary-50 text-primary-700 font-semibold'
                      : 'border-ink-200 text-ink-700 bg-white',
                  )}
                >
                  {sc.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {Array.from({ length: value.count }, (_, i) => (
              <label key={i} className="block space-y-1">
                <span className="text-ink-600 text-xs font-semibold">Speaker {i + 1}</span>
                <input
                  className="border-ink-200 text-ink-900 min-h-[2.75rem] w-full rounded-lg border bg-white px-3 text-sm"
                  maxLength={ROLE_MAX}
                  value={value.roles[i] ?? ''}
                  onChange={(e) => setRole(i, e.target.value)}
                />
              </label>
            ))}
          </div>
          {duplicateRoleNames(value) && (
            <p className="text-warning-700 text-[13px]">
              Deux rôles ont le même nom : donne-leur des noms différents.
            </p>
          )}
          <p className="text-ink-600 text-[13px]">
            Chaque réplique est dite par un rôle : dans « Audios animations », chacun enregistre les
            répliques de son Speaker.
          </p>
        </>
      ) : (
        <>
          <label className="block space-y-1">
            <span className="text-ink-600 text-xs font-semibold">Nom de la voix</span>
            <input
              className="border-ink-200 text-ink-900 min-h-[2.75rem] w-full rounded-lg border bg-white px-3 text-sm"
              maxLength={ROLE_MAX}
              value={value.roles[0] ?? ''}
              onChange={(e) => setRole(0, e.target.value)}
            />
          </label>
          <p className="text-ink-600 text-[13px]">
            Une seule voix, mais plusieurs personnes peuvent l’enregistrer : une scène chacune.
          </p>
        </>
      )}

      <label className="block space-y-1" htmlFor={`${id}-notes`}>
        <span className="text-ink-600 text-xs font-semibold">
          {dialogue
            ? 'Indications sur le dialogue (facultatif)'
            : 'Indications sur la voix (facultatif)'}
        </span>
        <textarea
          id={`${id}-notes`}
          className="border-ink-200 text-ink-900 w-full rounded-lg border bg-white px-3 py-2 text-sm"
          rows={2}
          maxLength={NOTES_MAX}
          placeholder={
            dialogue
              ? 'L’élève se trompe d’abord, le prof le remet sur la piste, ton complice et drôle…'
              : 'Ton calme, comme un grand frère qui explique…'
          }
          value={value.notes}
          onChange={(e) => onChange({ ...value, notes: e.target.value })}
        />
      </label>
    </fieldset>
  );
}
